/**
 * Flat contract billing periods (tracker item 34). A port of backend
 * recurring-jobs/billing-periods.ts: periods are anchored at the schedule's
 * start date and month arithmetic is always from the anchor, so a contract
 * started on the 31st bills on the 31st or the month's last day without
 * drifting. Every date is a YYYY-MM-DD string (UTC).
 *
 * The mock client uses it to preview and bill offline.
 */

import { FlatContractBillingFrequency } from '../api/types';
import { addDays, addMonths } from './recurrence';

export interface BillingPeriodPattern {
  anchor: string;
  frequency: FlatContractBillingFrequency;
  interval: number;
}

export interface BillingPeriod {
  index: number;
  start: string;
  end: string;
}

const MONTHS_PER_UNIT: Record<Exclude<FlatContractBillingFrequency, 'WEEKLY'>, number> = {
  MONTHLY: 1,
  QUARTERLY: 3,
  ANNUALLY: 12,
};

export function billingPeriodStart(pattern: BillingPeriodPattern, index: number): string {
  if (pattern.frequency === 'WEEKLY') return addDays(pattern.anchor, 7 * pattern.interval * index);
  return addMonths(pattern.anchor, MONTHS_PER_UNIT[pattern.frequency] * pattern.interval * index);
}

export function billingPeriodAt(pattern: BillingPeriodPattern, index: number): BillingPeriod {
  return { index, start: billingPeriodStart(pattern, index), end: addDays(billingPeriodStart(pattern, index + 1), -1) };
}

/** The period containing `date`, or null before the anchor. */
export function billingPeriodContaining(pattern: BillingPeriodPattern, date: string): BillingPeriod | null {
  if (date < pattern.anchor) return null;
  let index = 0;
  while (billingPeriodStart(pattern, index + 1) <= date) index += 1;
  return billingPeriodAt(pattern, index);
}

/** True when the two inclusive date ranges share a day. */
export function periodsOverlap(a: { start: string; end: string }, b: { start: string; end: string }): boolean {
  return a.start <= b.end && b.start <= a.end;
}
